// src/components/AIChatBot.jsx
import React, { useState, useRef, useEffect } from 'react';
import './AIChatBot.css';

function AIChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: '👋 Hi! I\'m an AI assistant who knows all about Milorad. Ask me about his skills, projects, experience, or anything else! Any feedback would be appreciated, feel free to send to any contact' 
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question: input })
      });

      const data = await response.json();
      
      if (data.success && data.answer) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.answer 
        }]);
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
} catch (error) {
  console.error('Chat error:', error);
  
  const errorMessage = error.message || '😅 Sorry, I encountered an error. Please try asking again!';
  
  setMessages(prev => [...prev, { 
    role: 'assistant', 
    content: errorMessage
  }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    "What technologies does Milorad know?",
    "Tell me about his projects",
    "What is he currently learning?",
    "How can I contact him?"
  ];

  const handleSuggestedQuestion = (question) => {
    setInput(question);
  };

  return (
    <div className="chatbot-container">
      {/* Floating chat button */}
      <button 
        className={`chat-bubble ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle chat"
      >
        {isOpen ? <span className="close-x" >✕</span> : (
  <img 
    src="/robot-icon.png" 
    alt="AI Assistant" 
    className="chat-icon"
  />
)}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-header-content">
              <div className="chat-avatar">🤖</div>
              <div>
                <h3>Ask About Milorad</h3>
                <p className="chat-status">
                  <span className="status-dot"></span>
                  AI Assistant
                </p>
              </div>
            </div>
            <button 
              className="close-button" 
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="chat-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.role}`}>
                <div className="message-content">
                  {msg.content}
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="message assistant">
                <div className="message-content typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            {/* Suggested questions (only show at start) */}
            {messages.length === 1 && !loading && (
              <div className="suggested-questions">
                <p className="suggested-label">Try asking:</p>
                {suggestedQuestions.map((question, idx) => (
                  <button
                    key={idx}
                    className="suggested-question"
                    onClick={() => handleSuggestedQuestion(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <div className="chat-input">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                disabled={loading}
                aria-label="Type your message"
              />
              <button 
                onClick={sendMessage} 
                disabled={loading || !input.trim()}
                className="send-button"
                aria-label="Send message"
              >
                {loading ? '⏳' : '📤'}
              </button>
            </div>
            <p className="chat-disclaimer">
              AI-powered assistant • May occasionally make mistakes
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIChatBot;
